import { defineStore, acceptHMRUpdate } from 'pinia';
import type { User } from '~/types/app';
import { getServices } from '~/db/services';

export const useUserStore = defineStore('UserStore', () => {
  if (!useAppRunningStore().isRunning) return;
  const data = ref<User>(null);

  const { $db } = useNuxtApp();
  const { usersService } = getServices($db);

  const { user: userDataSync } = useUserSyncGet();

  const { $sentrySetUser } = useNuxtApp();

  watchEffect(() => {
    if (userDataSync.value && userDataSync.value.length > 0) {
      data.value = userDataSync.value[0].data;
    } else {
      data.value = null;
    }
  });

  const loadFromClient = async () => {
    const context = useContextStore();
    const userData = await usersService.get({ id: context.userId });
    if (userData) {
      forceUpdateRawValue(userData.data);
      return true;
    }
    return false;
  };

  const forceUpdateRawValue = (rawValue) => {
    globalThis.$logger.info('useUserStore forceUpdateRawValue', rawValue);
    data.value = rawValue;
  };

  const email = computed(() => data.value?.email ?? '');

  const identifier = computed(() => data.value?.email ?? '');

  const firstname = computed(() => {
    return data.value?.firstname ?? '';
  });

  const lastname = computed(() => {
    return data.value?.lastname ?? '';
  });

  const fullName = computed(() => {
    return `${firstname.value} ${lastname.value}`;
  });

  const birthdate = computed(() => {
    return data.value?.birthdate ?? '';
  });

  const id = computed(() => data?.value?.id ?? null);

  watchEffect(() => {
    if (id.value && email.value) {
      $sentrySetUser({
        id: id.value,
        email: email.value,
        name: fullName.value,
      });
    }
  });

  return {
    data: data as Readonly<Ref<User>>,
    forceUpdateRawValue,
    loadFromClient,
    firstname,
    lastname,
    fullName,
    email,
    birthdate,
    id,
    identifier,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
}
